import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CostingService } from '../../services/costing.service';
import { AuthService } from '../../services/auth.service';
import { AbsorptionRun } from '../../interfaces/costing.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';

/** Absorption runs only for completed months, so the page opens on last month. */
const previousMonthStart = (): string => {
  const [y, m] = toLocalIsoDate().split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const AbsorptionRuns: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.COSTING_MANAGE);
  const [runs, setRuns] = useState<AbsorptionRun[]>([]);
  const [period, setPeriod] = useState(previousMonthStart());
  const [open, setOpen] = useState<AbsorptionRun | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRuns(await CostingService.listRuns(institutionId)); }
    catch (err) { toast.showApiError(err, 'Could not load absorption runs'); }
  }, [institutionId, toast]);

  useEffect(() => { load(); }, [load]);

  const act = async (label: string, fn: () => Promise<AbsorptionRun>) => {
    setBusy(true);
    try { const r = await fn(); setOpen(r); toast.showSuccess(label); await load(); }
    catch (err) { toast.showApiError(err, `${label} failed`); }
    finally { setBusy(false); }
  };

  return (
    <Box sx={{ p: 2 }} data-help-page="costing.absorption.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Overhead absorption</Typography>
        <Button data-help="costing.absorption.orders" component={RouterLink} to="/costing/orders">Order costs</Button>
        <Button data-help="costing.absorption.settings" component={RouterLink} to="/costing/settings">Settings</Button>
      </Box>
      {canManage && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }} data-help-section="costing.absorption.form">
          <TextField data-help="costing.absorption.month" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="date" label="Month" InputLabelProps={{ shrink: true }}
            value={period} onChange={e => setPeriod(e.target.value)} />
          <Button data-help="costing.absorption.preview" variant="outlined" disabled={busy} onClick={() => act('Preview computed', () => CostingService.previewRun(institutionId, period))}>Preview</Button>
        </Paper>
      )}
      <TableContainer component={Paper} sx={{ mb: 2 }} data-help-section="costing.absorption.table">
        <Table size="small" data-testid="runs-table">
          <TableHead><TableRow><TableCell data-help="costing.absorption.col.period">Period</TableCell><TableCell data-help="costing.absorption.col.status">Status</TableCell><TableCell align="right" data-help="costing.absorption.col.pool">Pool</TableCell>
            <TableCell align="right" data-help="costing.absorption.col.absorbed">Absorbed</TableCell><TableCell align="right" data-help="costing.absorption.col.notAbsorbed">Not absorbed</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {runs.map(r => (
              <TableRow key={r.id} hover><TableCell>{r.periodStart} - {r.periodEnd}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={r.status === 'POSTED' ? 'success' : r.status === 'REVERSED' ? 'default' : 'info'} /></TableCell>
                <TableCell align="right">{formatMoney(r.poolTotal)}</TableCell><TableCell align="right">{formatMoney(r.absorbedTotal)}</TableCell>
                <TableCell align="right">{formatMoney(r.notAbsorbedTotal)}</TableCell>
                <TableCell align="right"><Button data-help="costing.absorption.open" size="small" onClick={() => act('Loaded', () => CostingService.getRun(r.id))}>Open</Button></TableCell></TableRow>
            ))}
            {runs.length === 0 && <TableRow><TableCell colSpan={6}>No runs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      {open && (
        <Paper sx={{ p: 2 }} data-testid="run-detail" data-help-section="costing.absorption.detail">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ flex: '1 1 240px' }}>{open.periodStart} - {open.periodEnd} ({open.driver})</Typography>
            {canManage && open.status === 'DRAFT' && <Button data-help="costing.absorption.post" variant="contained" disabled={busy} onClick={() => act('Run posted', () => CostingService.postRun(open.id))}>Post</Button>}
            {canManage && open.status === 'POSTED' && <Button data-help="costing.absorption.reverse" color="error" disabled={busy} onClick={() => act('Run reversed', () => CostingService.reverseRun(open.id))}>Reverse</Button>}
          </Box>
          {open.lines.some(l => l.error) && <Alert severity="error" sx={{ mb: 1 }}>Some segments failed to post; fix the cause and post again.</Alert>}
          <Table size="small">
            <TableHead><TableRow><TableCell data-help="costing.absorption.line.kind">Kind</TableCell><TableCell data-help="costing.absorption.line.order">Order</TableCell><TableCell align="right" data-help="costing.absorption.line.driver">Driver</TableCell><TableCell align="right" data-help="costing.absorption.line.amount">Amount</TableCell><TableCell data-help="costing.absorption.line.error">Error</TableCell></TableRow></TableHead>
            <TableBody>
              {open.lines.map(l => (
                <TableRow key={l.id}><TableCell>{l.kind}</TableCell><TableCell>{l.orderNumber ?? '-'}</TableCell>
                  <TableCell align="right">{l.driverValue ?? '-'}</TableCell><TableCell align="right">{formatMoney(l.amount)}</TableCell><TableCell>{l.error}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default AbsorptionRuns;
